import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAppSelector } from 'redux/store';
import { useSession } from 'next-auth/react';

import { Breadcrumb } from 'components/breadcrumb/breadcrumb';
import { Search } from 'components/search/search';
import { Button } from 'components/button';
import { TabFilter } from 'components/tab-filter/tab-filter';
import { ITabItem } from 'components/tab-filter/tab-item/tab-item';

import styles from './search-header.module.scss';
import { getDomain } from 'utils/global-search';
import { useType } from 'modules/search-overview/queries';

export interface ICover {
	url?: string;
	altText?: string;
}

export interface ITag {
	name: string;
	id: number;
	__typename: string;
}

export interface IHeaderProps {
	fullPath: string;
	headerTitle: string;
	searchPlaceholder: string;
	searchResultItems: any[];
	internalNotes: any[];
	search: string;
	isLoading: boolean;
	handleSearchOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const SearchHeader = (props: IHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { data: session } = useSession();

	const [tabItem, setTabItems] = useState<ITabItem[]>([]);
	const { showNeutralStyle } = useAppSelector((state) => state.settings);

	const { selectedType, setSelectedType } = useType();

	useEffect(() => {
		const domain = getDomain();
		const tabItems: ITabItem[] = [
			{
				id: 'pages',
				label: formatMessage({ id: 'modules.search.tabs.pages' }, { count: props.searchResultItems?.length || 0 })
			}
		];

		if (domain === 'gelderland' && session?.accessToken) {
			tabItems.push({
				id: 'internal',
				label: formatMessage(
					{ id: 'modules.search.tabs.internalInformation' },
					{ count: props.internalNotes?.length || 0 }
				)
			});
		}

		setTabItems(tabItems);

		if (!props.searchResultItems?.length && !!props.internalNotes?.length && tabItems.length === 2) {
			setSelectedType(tabItems[1]);
		}
	}, [props.searchResultItems, props.internalNotes]);

	const onTypeTabChange = (tabItemValue: ITabItem): void => {
		setSelectedType(tabItemValue);
	};

	return (
		<div className={clsx(styles.introHeader)}>
			<div className="container">
				<div className={styles.header}>
					<div className={styles.content}>
						{!showNeutralStyle && (
							<div className={styles.sidebar}>
								<div />
								<div />
								<div />
								<div />
							</div>
						)}
						<div className={styles.main}>
							<div className={styles.breadcrumb}>
								<Breadcrumb path={props.fullPath} />
							</div>
							<div className={styles.title}>
								<h1>{props.headerTitle}</h1>
							</div>
							<div className={styles.search}>
								<form
									onSubmit={(event: any) => {
										event.preventDefault();
										// onSearch();
									}}
								>
									<Search
										searchValue={props.search || ''}
										searchPlaceholderText={props.searchPlaceholder}
										handleSearchOnChange={props.handleSearchOnChange}
										high
										hideIcon
									/>
								</form>
								<Button
									variant="primary"
									title={formatMessage({ id: 'global.search' })}
									onClick={() => {
										// onSearch();
									}}
								/>
							</div>
						</div>
					</div>
				</div>
				{props.isLoading && !props.searchResultItems?.length && !props.internalNotes?.length && (
					<div className={styles.loadingState}>
						<div className={styles.loading}>
							<div className={styles.loadingBar} />
							<div className={styles.loadingBar} />
						</div>
					</div>
				)}
				{(!!props.searchResultItems?.length || !!props.internalNotes?.length) && (
					<div className={styles.tabs}>
						<TabFilter
							title={formatMessage({ id: 'modules.searchHeader.filter' })}
							tabItems={tabItem}
							onChange={onTypeTabChange}
							selectedTabItem={selectedType}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default SearchHeader;
